import type { AppStrings } from '@veylme/shared';

import { ApiAuthError, ApiError, ApiParseError, ApiRateLimitError } from '../api/errors.js';

/**
 * Превращает любую пойманную ошибку в человекочитаемую строку для показа пользователю.
 *
 * Правила:
 * - Маппинг идёт по `err.status` (для ApiError) с уточнением по `err.code` где нужно.
 * - `err.message` в UI НЕ показывается — только через этот маппинг.
 * - Сетевой сбой (fetch reject, TypeError) → `err_network`.
 * - Несоответствие схемы 2xx-ответа (ApiParseError) → `err_schema` (НЕ «нет сети»).
 * - Любой неизвестный случай → `err_generic`.
 *
 * Используется везде где catch показывает ошибку пользователю:
 *   `showError(humanizeApiError(err, t))`
 */
export function humanizeApiError(err: unknown, t: AppStrings): string {
  // work-024 D5: несоответствие схемы — НЕ сетевая ошибка, отдельный тип.
  if (err instanceof ApiParseError) {
    return t.err_schema;
  }

  // work-022: 403 sender_banned — проверяем ДО switch(err.status), иначе 403
  // уходит в default → err_generic (CRIT-1 из plan-verifier).
  if (err instanceof ApiError && err.code === 'sender_banned') {
    return t.ask_error_banned;
  }

  if (err instanceof ApiError) {
    switch (err.status) {
      case 401:
        return t.ask_error_restart;
      case 429:
        return t.ask_error_rate_limit;
      case 400:
        return t.err_bad_request;
      case 500:
        return t.err_internal;
      case 502:
      case 503:
      case 504:
        return t.err_server_unavailable;
      default:
        // Остальные 4xx/5xx
        return t.err_generic;
    }
  }

  // Сетевой сбой: fetch reject (TypeError: Failed to fetch) или нет ответа.
  if (err instanceof TypeError || err instanceof Error) {
    return t.err_network;
  }

  return t.err_generic;
}

// Re-export для удобства импорта вместе с типами ошибок.
export { ApiError, ApiAuthError, ApiRateLimitError, ApiParseError };
